import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Button,
  Col,
  Card,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { Image } from "react-bootstrap";
import { FaUser, FaLock } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";
import MicrosoftLogin from "react-microsoft-login";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";

import logoMicrosoft from "../assets/img/logo_microsoft.png";
import { selectUser } from "../store/selectors.js";
import { authenticate, authenticateSSO } from "../store/thunks.js";
import genericTranslations from "../utils/genericTranslations.js";
import {
  AuthenticationError,
  AUTHENTICATION_NOTOK,
  AUTHENTICATION_MISSING_TOKEN,
  CurrentUserError,
  AUTHENTICATION_TOO_MANY_ATTEMPTS,
} from "../utils/sessionErrors.js";

const messages = {
  authenticationMissingTokenMessage: {
    id: "layouts.Login.authenticationMissingTokenMessage",
    defaultMessage:
      "The server response did not contain an authentication token.",
  },
  authenticationNotOkMessage: {
    id: "layouts.Login.authenticationNotOkMessage",
    defaultMessage: "Couldn't login using the supplied credentials.",
  },
  authenticationUnknownMessage: {
    id: "layouts.Login.authenticationUnknownMessage",
    defaultMessage: "An unknown error occurred. Try again later.",
  },
  currentUserMissingMessage: {
    id: "layouts.Login.currentUserMissingMessage",
    defaultMessage:
      "Authentication was successful, but the user could not be found. Try again later.",
  },
  reachedLimitFailedAttempts: {
    id: "layouts.Login.reachedLimitFailedAttempts",
    defaultMessage:
      "You’ve reached the maximum number of login attempts. Please wait 30 minutes before trying again.",
  },
  loginInstructions: {
    id: "layouts.Login.loginInstructions",
    defaultMessage: "You can log in using your e-mail address as username.",
  },
  loginMicrosoftInstructions: {
    id: "layouts.Login.loginMicrosoftInstructions",
    defaultMessage: "Log in with Single Sign-On",
  },
  passwordLabel: {
    id: "layouts.Login.passwordLabel",
    defaultMessage: "Password",
  },
  passwordPlaceholder: {
    id: "layouts.Login.passwordPlaceholder",
    description: "Password placeholder",
    defaultMessage: "Enter your password here",
  },
  usernameLabel: {
    id: "layouts.Login.usernameLabel",
    defaultMessage: "Username",
  },
  usernamePlaceholder: {
    id: "layouts.Login.usernamePlaceholder",
    defaultMessage: "Enter your username here",
  },
  welcome: {
    id: "layouts.Login.welcome",
    defaultMessage: "Welcome to the openintranet",
  },
};

const { VITE_OIDC_TENANT_URL, VITE_OIDC_CLIENT_ID } = import.meta.env;

const Login = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(selectUser);

  const [alerts, setAlerts] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);

  // Redirect when the user is authenticated.
  useEffect(() => {
    if (user) {
      setRedirectToReferrer(true);
    }
  }, [user]);

  const login = useCallback(async () => {
    try {
      await dispatch(authenticate(username, password));
    } catch (error) {
      let alertMessage;

      if (error instanceof AuthenticationError) {
        switch (error.type) {
          case AUTHENTICATION_NOTOK:
            alertMessage = formatMessage(messages.authenticationNotOkMessage);
            break;
          case AUTHENTICATION_TOO_MANY_ATTEMPTS:
            alertMessage = formatMessage(messages.reachedLimitFailedAttempts);
            break;
          case AUTHENTICATION_MISSING_TOKEN:
            alertMessage = formatMessage(
              messages.authenticationMissingTokenMessage,
            );
            break;
          default:
            alertMessage = formatMessage(messages.authenticationUnknownMessage);
        }
      } else if (error instanceof CurrentUserError) {
        alertMessage = formatMessage(messages.currentUserMissingMessage);
      } else {
        alertMessage = formatMessage(messages.authenticationUnknownMessage);
      }

      setAlerts([
        {
          id: `authentication-${Date.now()}`,
          style: "danger",
          message: alertMessage,
        },
      ]);
    }
  }, [dispatch, username, password, formatMessage]);

  const handleMicrosoftLogin = useCallback(
    async (error, authData, msal) => {
      if (error || !authData) return;

      const { idToken } = authData;

      if (!idToken) {
        setAlerts([
          {
            id: `authentication-microsoft-missing-token-${Date.now()}`,
            style: "danger",
            message: formatMessage(messages.authenticationMissingTokenMessage),
          },
        ]);
        return;
      }

      try {
        await dispatch(authenticateSSO(idToken, msal));
      } catch (error) {
        if (error instanceof AuthenticationError) {
          setAlerts([
            {
              id: `authentication-microsoft-unknown-${Date.now()}`,
              style: "danger",
              message: formatMessage(messages.authenticationUnknownMessage),
            },
          ]);
        }
      }
    },
    [dispatch, formatMessage],
  );

  const displayAlerts = useCallback(() => {
    return alerts.map((alert) => (
      <Alert
        key={alert.id}
        variant={alert.style}
        dismissible
        onClose={() => setAlerts(alerts.filter((a) => a.id !== alert.id))}
      >
        {alert.message}
      </Alert>
    ));
  }, [alerts]);

  // Determine redirect target.
  const queryMatches = /(\?|&)from=([^&]+)/.exec(location.search);
  const queryPathname =
    (queryMatches && queryMatches.length === 3 && queryMatches[2]) || "/";
  const from =
    (location.state && location.state.from && location.state.from.pathname) ||
    queryPathname;

  if (redirectToReferrer) {
    return <Navigate to={from} replace />;
  }

  return (
    <Row>
      <Col sm={{ span: 6, offset: 3 }}>
        <Card className="card-primary">
          <Card.Header as="h3" className="card-extra-padding">
            <FormattedMessage {...messages.welcome} />
          </Card.Header>

          <Card.Body className="card-extra-padding">
            <Row>
              <Col lg={8}>
                <p>
                  <FormattedMessage {...messages.loginInstructions} />
                </p>

                {displayAlerts()}

                <Form
                  onSubmit={(event) => {
                    login();
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                >
                  <Form.Group className="mb-3" controlId="username">
                    <Form.Label>
                      <FormattedMessage {...messages.usernameLabel} />
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaUser color="gray" />
                      </InputGroup.Text>
                      <Form.Control
                        suppressHydrationWarning
                        type="text"
                        value={username}
                        placeholder={formatMessage(
                          messages.usernamePlaceholder,
                        )}
                        onChange={(event) => setUsername(event.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="password">
                    <Form.Label>
                      <FormattedMessage {...messages.passwordLabel} />
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaLock color="gray" />
                      </InputGroup.Text>
                      <Form.Control
                        suppressHydrationWarning
                        type="password"
                        value={password}
                        placeholder={formatMessage(
                          messages.passwordPlaceholder,
                        )}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Button
                      type="submit"
                      className="text-center"
                      as="input"
                      variant="primary"
                      value={formatMessage(genericTranslations.login)}
                    />
                  </Form.Group>
                </Form>
              </Col>
              <Col lg={4} className="microsoft-login">
                <MicrosoftLogin
                  debug
                  buttonTheme="light"
                  clientId={VITE_OIDC_CLIENT_ID}
                  tenantUrl={VITE_OIDC_TENANT_URL}
                  authCallback={(error, authData, msal) => {
                    if (!error && authData) {
                      handleMicrosoftLogin(error, authData, msal);
                    }
                  }}
                  prompt={"select_account"}
                  withUserData
                  graphScopes={["user.read"]}
                  forceRedirectStrategy
                  useLocalStorageCache
                >
                  <Button variant="primary" className="login-button">
                    <Image src={logoMicrosoft} style={{ marginRight: 10 }} />
                    SignIn with Microsoft
                  </Button>
                </MicrosoftLogin>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

Login.propTypes = {
  user: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string,
    state: PropTypes.shape({
      from: PropTypes.shape({
        pathname: PropTypes.string,
      }),
    }),
  }),
};

export default Login;
